import { useRef, useCallback, useState, useEffect } from 'react';

export default function HeroSection() {
  const heroRef = useRef<HTMLElement>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const scrollToSection = useCallback((sectionId: string) => {
    requestAnimationFrame(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    });
  }, []);

  const handleLinkClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    e.preventDefault();
    scrollToSection(sectionId);
  }, [scrollToSection]);

  // Preload hero image for better performance
  useEffect(() => {
    const img = new Image();
    img.onload = () => setImageLoaded(true);
    img.onerror = () => setImageError(true);
    img.src = 'https://public.readdy.ai/ai/img_res/8a7068a1525c74f022eec0d3bdc9c7b7.jpg';
  }, []);

  const heroImageUrl = 'https://readdy.ai/api/search-image?query=Modern%20enterprise%20data%20center%20with%20server%20racks%20and%20network%20infrastructure%2C%20consistent%20blue%20to%20teal%20gradient%20ambient%20lighting%20throughout%20the%20entire%20facility%2C%20uniform%20LED%20strip%20lighting%20creating%20seamless%20color%20transitions%20from%20deep%20blue%20on%20left%20to%20bright%20teal%20on%20right%2C%20professional%20corporate%20technology%20environment%20with%20consistent%20gradient%20illumination%2C%20clean%20organized%20server%20room%20with%20blue-teal%20gradient%20lighting%20system%20covering%20all%20walls%20and%20equipment%2C%20enterprise%20networking%20hardware%20with%20uniform%20blue%20to%20teal%20color%20scheme%20lighting%2C%20pristine%20data%20center%20environment%20with%20smooth%20gradient%20colored%20ambient%20lighting%20from%20blue%20to%20teal%20across%20the%20entire%20space&width=1920&height=1080&seq=datacenter-consistent-gradient&orientation=landscape';

  // Generate responsive image sources
  const generateResponsiveImages = () => {
    const baseUrl = 'https://readdy.ai/api/search-image?query=Modern enterprise data center with server racks and network infrastructure, consistent blue to teal gradient ambient lighting throughout the entire facility, uniform LED strip lighting creating seamless color transitions from deep blue on left to bright teal on right, professional corporate technology environment with consistent gradient illumination, clean organized server room with blue-teal gradient lighting system covering all walls and equipment, enterprise networking hardware with uniform blue to teal color scheme lighting, pristine data center environment with smooth gradient colored ambient lighting from blue to teal across the entire space&seq=datacenter-consistent-gradient&orientation=landscape';
    
    return {
      mobile: `${baseUrl}&width=768&height=432`,
      tablet: `${baseUrl}&width=1024&height=576`,
      desktop: `${baseUrl}&width=1920&height=1080`,
      large: `${baseUrl}&width=2560&height=1440`
    };
  };

  const responsiveImages = generateResponsiveImages();

  return (
    <section 
      ref={heroRef}
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{
        backgroundImage: imageLoaded ? `url('${heroImageUrl}')` : 'linear-gradient(135deg, #1e3a8a 0%, #0f766e 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Responsive background images using picture element for better performance */}
      {imageLoaded && (
        <picture className="absolute inset-0 w-full h-full">
          <source 
            media="(min-width: 1536px)" 
            srcSet={responsiveImages.large}
          />
          <source 
            media="(min-width: 1024px)" 
            srcSet={responsiveImages.desktop}
          />
          <source 
            media="(min-width: 768px)" 
            srcSet={responsiveImages.tablet}
          />
          <img 
            src={responsiveImages.mobile}
            alt="Modern data center with blue-teal gradient lighting"
            className="w-full h-full object-cover"
            loading="eager"
            decoding="sync"
            fetchPriority="high"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center'
            }}
          />
        </picture>
      )}

      {/* Loading placeholder */}
      {!imageLoaded && !imageError && (
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-blue-800 to-teal-700 animate-pulse">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-pulse"></div>
        </div>
      )}

      {/* Enhanced gradient overlay for better text contrast */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/75 via-blue-800/70 to-teal-700/75"></div>
      
      {/* Additional overlay for text readability */}
      <div className="absolute inset-0 bg-black/20"></div>
      
      <div className="hero-content relative z-10">
        <div className="text-center text-white">
          {/* Award Banner */}
          <div className="mb-8 flex justify-center">
            <div
              className="inline-flex items-center px-12 py-3 shadow-lg"
              style={{
                clipPath: 'polygon(22px 0%, calc(100% - 22px) 0%, 100% 50%, calc(100% - 22px) 100%, 22px 100%, 0% 50%)',
                background: 'linear-gradient(90deg, #92610a 0%, #d4a017 30%, #f5c842 50%, #d4a017 70%, #92610a 100%)'
              }}
            >
              <i className="ri-award-fill text-white text-xl mr-3 w-6 h-6 flex items-center justify-center flex-shrink-0" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.4)' }}></i>
              <span className="font-bold text-lg whitespace-nowrap" style={{ color: '#3b1f00', textShadow: '0 1px 1px rgba(255,255,255,0.25)' }}>A Top Virginia MSP for Nonprofits</span>
              <i className="ri-award-fill text-white text-xl ml-3 w-6 h-6 flex items-center justify-center flex-shrink-0" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.4)' }}></i>
            </div>
          </div>

          {/* Critical content - enhanced for better visibility */}
          <div className="mb-8">
            <h1 className="hero-title text-shadow-lg">
              Helping Virginia Nonprofits
              <span className="hero-subtitle block text-teal-300 mt-2">Leverage Technology to Achieve the Mission</span>
            </h1>
            <p className="hero-description text-shadow-md">
              Professional IT managed services designed specifically for nonprofit organizations. 
              That's what tekFLOW is.
              Reliable, secure, and budget-friendly solutions that let you focus on your mission.
            </p>
          </div>

          {/* Buttons with enhanced styling */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            <a 
              href="#consultation"
              onClick={(e) => handleLinkClick(e, 'consultation')}
              className="inline-flex items-center justify-center px-8 py-4 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg transition-all duration-200 w-full sm:w-auto whitespace-nowrap text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1 backdrop-blur-sm"
              aria-label="Schedule a free consultation"
            >
              <i className="ri-calendar-line mr-2 w-4 h-4 flex items-center justify-center flex-shrink-0"></i>
              Get Free Consultation
            </a>
            <a 
              href="#services"
              onClick={(e) => handleLinkClick(e, 'services')}
              className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-blue-600 font-semibold rounded-lg transition-all duration-200 w-full sm:w-auto whitespace-nowrap text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1 backdrop-blur-sm"
              aria-label="View our IT services"
            >
              <i className="ri-service-line mr-2 w-4 h-4 flex items-center justify-center flex-shrink-0"></i>
              View Our Services
            </a>
          </div>
          
          {/* Stats Grid with enhanced styling */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-8 text-center">
            <div className="bg-white/15 backdrop-blur-md rounded-lg p-4 sm:p-6 border border-white/20 shadow-lg">
              <div className="text-2xl sm:text-3xl font-bold text-teal-300 mb-2">100%</div>
              <div className="text-sm sm:text-lg text-white/90">Nonprofit Focused</div>
            </div>
            <div className="bg-white/15 backdrop-blur-md rounded-lg p-4 sm:p-6 border border-white/20 shadow-lg">
              <div className="text-2xl sm:text-3xl font-bold text-teal-300 mb-2">99.9%</div>
              <div className="text-sm sm:text-lg text-white/90">Uptime Guarantee</div>
            </div>
            <div className="bg-white/15 backdrop-blur-md rounded-lg p-4 sm:p-6 border border-white/20 shadow-lg">
              <div className="text-2xl sm:text-3xl font-bold text-teal-300 mb-2">24/7</div>
              <div className="text-sm sm:text-lg text-white/90">Support Portal Access</div>
            </div>
          </div>
        </div>
      </div>

      {/* Performance optimization: Preload next section images */}
      <link 
        rel="preload" 
        as="image" 
        href="https://readdy.ai/api/search-image?query=Professional%20IT%20support%20team%20working%20in%20modern%20office%20environment&width=800&height=600&seq=services-preview&orientation=landscape"
      />
    </section>
  );
}
